<script setup>
import AutoNumeric from 'autonumeric';
import { isEmpty } from 'lodash';
import { esNumeroValido } from '@/libs/numbers';
import {
  computed, onMounted, onUnmounted, ref, watch,
} from 'vue';
import { Configuraciones } from '../../libs/autonumeric';
import toast from '../../libs/cifra/toast';

const props = defineProps({
  size: { type: String, default: undefined },
  // Rails devuelve números decimales como "8.25", son Strings
  modelValue: { type: [Number, String], default: null },
  /**
   * configuración a aplicar para autonumeric. Está la lista de configuraciones
   * en ../../libs/autonumeric (entero, decimal: porcentaje, precio, nota)
   */
  configuracion: { type: String, default: 'entero' },
  /**
   * Si se indica un inputName, pondrá un input oculto con el valor real
   * del input numérico (el valor sin formatear)
   */
  inputName: { type: String, default: undefined },
  min: { type: Number, default: null },
  max: { type: Number, default: null },
});

const emits = defineEmits(['update:modelValue']);
const inputClasses = computed(() => (
  `form-control input-${props.size}`
));
const input = ref();
let autonumeric;

// Devuelve true si se asignó correctamente
function asignarValorAutonumeric(valor) {
  try {
    if (esNumeroValido(valor)) {
      autonumeric.set(valor);
    } else {
      autonumeric.clear();
    }
    return true;
  } catch (e) {
    toast.error(`El valor actual es ${valor} pero no es válido para este campo`);
    console.error('Error al asignar el valor', e);
    return false;
  }
}

function updateModelValue() {
  if (autonumeric) {
    let number = autonumeric.getNumber();
    if (number !== props.modelValue) {
      if (isEmpty(autonumeric.getNumericString())) {
        // Si es blanco, ponemos null. Cuando es blanco en el input, el método
        // getNumber de autonumeric nos devuelve 0 y nosotros queremos el null
        number = null;
      }
      emits('update:modelValue', number);
    }
  }
}

function cargarAutonumeric() {
  if (autonumeric) {
    // Quita el valor y elimina el autonumeric anterior.
    autonumeric.wipe();
  }
  const configuracion = { ...Configuraciones[props.configuracion] };
  if (props.max) {
    configuracion.maximumValue = `${props.max}.00`;
  }
  if (props.min) {
    configuracion.minimumValue = props.min;
    if (props.min > 0) {
      // Para quitar un warning
      configuracion.overrideMinMaxLimits = 'invalid';
    }
  }
  autonumeric = new AutoNumeric(input.value, configuracion);
  if (props.modelValue !== undefined && props.modelValue !== null) {
    if (asignarValorAutonumeric(props.modelValue)) {
      updateModelValue();
    }
  }
}

watch(() => props.modelValue, () => {
  const number = autonumeric.getNumber();
  // Si es el mismo valor es que lo han modificado editando el input,
  // no hay que volver a asignar el valor
  if (number !== props.modelValue) {
    asignarValorAutonumeric(props.modelValue);
  }
});

watch(() => props.configuracion, () => {
  cargarAutonumeric();
});

onMounted(() => {
  cargarAutonumeric();
});

onUnmounted(() => {
  if (autonumeric) {
    autonumeric.remove();
    autonumeric = null;
  }
});

function lanzarEventoChange() {
  const event = new Event('change');
  input.value.dispatchEvent(event);
}

// Lanzamos el evento change del input para que lo puedan escuchar
// Se usa por ejemplo en las unidades de la edición de suscripción
watch(() => props.modelValue, () => {
  lanzarEventoChange();
});

function select() {
  this.$refs.input.select();
}

// Los usa el selector numérico
defineExpose({
  select,
  asignarValorAutonumeric,
});
</script>

<template>
  <input
    ref="input"
    :class="inputClasses"
    v-bind="$attrs"
    type="text"
    autocomplete="off"
    @autoNumeric:rawValueModified="updateModelValue"
  >
  <input
    v-if="inputName !== undefined"
    type="hidden"
    :name="inputName"
    :value="modelValue"
  />
</template>
